import { produce } from 'immer'

// Action Types
export const SET_PREVIEW = 'callAssetExports/setPreview'
export const CLEAR_PREVIEW = 'callAssetExports/clearPreview'
export const SET_PREVIEW_ERROR = 'callAssetExports/setPreviewError'
export const SET_EXPORT = 'callAssetExports/setExport'
export const SET_EXPORT_NOT_FOUND = 'callAssetExports/setExportNotFound'
export const SET_EXPORT_FORBIDDEN = 'callAssetExports/setExportForbidden'
export const SET_EXPORTS = 'callAssetExports/setExports'
export const SET_EXPORTS_FORBIDDEN = 'callAssetExports/setExportsForbidden'
export const SET_EXPORTS_ORGANIZATION = 'callAssetExports/setExportsOrganization'
export const SET_LOADING = 'callAssetExports/setLoading'

// Action Creators
export const setPreview = (payload) => ({ type: SET_PREVIEW, payload })
export const clearPreview = () => ({ type: CLEAR_PREVIEW })
export const setPreviewError = () => ({ type: SET_PREVIEW_ERROR })
export const setExport = (payload) => ({ type: SET_EXPORT, payload })
export const setExportNotFound = (payload) => ({ type: SET_EXPORT_NOT_FOUND, payload })
export const setExportForbidden = (payload) => ({ type: SET_EXPORT_FORBIDDEN, payload })
export const setExports = (payload) => ({ type: SET_EXPORTS, payload })
export const setExportsForbidden = (payload) => ({ type: SET_EXPORTS_FORBIDDEN, payload })
export const setExportsOrganization = (payload) => ({
  type: SET_EXPORTS_ORGANIZATION,
  payload,
})
export const setLoading = (payload) => ({ type: SET_LOADING, payload })

// State
export const initialState = {
  // What the modal shows before the user commits: exact counts and duration,
  // plus an approximate size. The precise size only exists once the job has
  // run its HEAD sweep, seconds in, so this is deliberately the rough one.
  preview: null,

  // Distinguishes "the preview failed" from "the preview has not arrived",
  // which otherwise look identical to the modal and leave it waiting forever.
  previewFailed: false,

  // The job currently being watched on the job page, polled while it runs.
  currentExport: null,

  // The id a 404 was answered for, or null. A 404 is an expected-empty
  // navigation -- a stale notification link, an export that has aged out, or
  // one belonging to another organization, which the API answers 404 for alike
  // so ids cannot be enumerated. It drives an empty state rather than a toast.
  //
  // It holds the id rather than a boolean because a reply can land after the
  // user has moved on: a late 404 for the export they just left would
  // otherwise put the empty state on the one they are looking at, and the poll
  // stops on this flag, so nothing would ever clear it.
  exportNotFound: null,

  // The id a load was refused for, or null. Separate from exportNotFound
  // because a 403 is not a missing export: the row exists and someone else
  // can open it. Held as an id for the same reason -- a refusal for the
  // export the user just left must not strand the one they are looking at.
  exportForbidden: null,

  // The organization the list is currently for. Deliberately in the store
  // rather than only in the page's own state: a response that arrives after
  // the user has switched organizations has to be recognised as stale and
  // dropped, and only the store knows what is on screen now.
  exportsOrganizationId: null,

  // The organization id a list load was refused for, or null. Held as an id
  // rather than a flag for the same reason exportForbidden is: an org-1 user
  // can switch the list between organizations, and a refusal for the one
  // they just left must not put the empty state on the one they are looking
  // at now.
  exportsForbidden: null,

  // Recent exports for the organization. Organization-wide rather than
  // personal because anyone with Cloud access can open a finished export --
  // a list showing only your own would hide the colleague's export you were
  // sent a link to, which is the case the list exists to rescue.
  exports: [],

  loading: {
    preview: false,
    create: false,
    currentExport: false,
    exports: false,
  },
}

export default (state = initialState, { type, payload } = {}) =>
  produce(state, (newState) => {
    switch (type) {
      case SET_PREVIEW:
        newState.preview = payload
        newState.previewFailed = false
        break

      case SET_PREVIEW_ERROR:
        newState.preview = null
        newState.previewFailed = true
        break

      case CLEAR_PREVIEW:
        // The preview describes one specific selection. Leaving a stale one in
        // place would let a modal opened over a different selection show the
        // previous counts for a moment, which is the kind of thing a user
        // reads and believes.
        newState.preview = null
        newState.previewFailed = false
        break

      case SET_EXPORT:
        newState.currentExport = payload
        break

      case SET_EXPORTS_FORBIDDEN:
        newState.exportsForbidden = payload
        break

      case SET_EXPORTS_ORGANIZATION:
        newState.exportsOrganizationId = payload
        break

      case SET_EXPORT_NOT_FOUND:
        newState.exportNotFound = payload
        break

      case SET_EXPORT_FORBIDDEN:
        newState.exportForbidden = payload
        break

      case SET_EXPORTS:
        newState.exports = payload
        break

      case SET_LOADING:
        newState.loading = { ...state.loading, ...payload }
        break

      default:
        break
    }
  })
